import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiSearch, FiSettings, FiShield, FiCpu, FiLock, FiActivity } = FiIcons;

const ProcessSection = () => {
  useEffect(() => {
    // SEO: Add structured data for this section
    const sectionData = {
      "@context": "https://schema.org", 
      "@type": "HowTo", 
      "name": "Executive AI Security Implementation Process",
      "description": "Proprietary three-phase methodology for deploying enterprise-grade private AI systems",
      "step": [
        { 
          "@type": "HowToStep", 
          "name": "Phase I: Strategic Assessment & Risk Intelligence", 
          "text": "Comprehensive security audit and threat modeling of your current AI infrastructure, identifying vulnerabilities, compliance gaps, and potential attack vectors specific to your enterprise ecosystem."
        },
        { 
          "@type": "HowToStep", 
          "name": "Phase II: Bespoke Secure AI Architecture", 
          "text": "Design and deployment of custom-tailored AI solutions with zero-trust architecture, end-to-end encryption, and isolated infrastructure meeting your exact security, performance, and compliance requirements."
        },
        { 
          "@type": "HowToStep", 
          "name": "Phase III: Continuous Defense & Elite Support", 
          "text": "24/7 elite security posture assessments, regular security audits, and rapid incident response protocols ensuring continuous protection and regulatory compliance."
        }
      ]
    };
    
    const scriptId = 'process-structured-data';
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(sectionData);
  }, []);

  const phases = [
    { 
      icon: FiSearch, 
      title: "Phase I", 
      subtitle: "Strategic Assessment",
      description: "Elite security audit and threat intelligence analysis of your current AI infrastructure, identifying critical vulnerabilities, compliance gaps, and sophisticated attack vectors specific to your enterprise ecosystem.",
      features: ["Advanced Penetration Testing", "Compliance Gap Analysis", "Threat Modeling", "Risk Quantification"]
    },
    { 
      icon: FiCpu, 
      title: "Phase II", 
      subtitle: "Bespoke Architecture",
      description: "Design and deployment of custom-tailored AI systems with zero-trust architecture, quantum-resistant encryption, and air-gapped infrastructure meeting your exact security, performance, and regulatory requirements.",
      features: ["Zero-Trust Implementation", "End-to-End Encryption", "Air-Gapped Deployment", "Custom Development"]
    },
    { 
      icon: FiShield, 
      title: "Phase III", 
      subtitle: "Continuous Defense",
      description: "24/7 elite security posture assessments, regular security audits, and rapid incident response protocols ensuring continuous protection and regulatory compliance.",
      features: ["24/7 Security Assessments", "Regular Security Audits", "Incident Response", "Compliance Management"]
    }
  ];

  return (
    <section className="exclusive-process py-24 relative" itemScope itemType="https://schema.org/HowTo">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 opacity-90" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          {/* Premium Badge */}
          <div className="inline-flex items-center justify-center mb-8">
            <div className="h-px bg-gradient-to-r from-transparent via-yellow-500 to-transparent w-20" />
            <div className="px-6">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
                <span className="text-yellow-500 text-sm font-bold uppercase tracking-widest">Proprietary Methodology</span>
                <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
              </div>
            </div>
            <div className="h-px bg-gradient-to-r from-transparent via-yellow-500 to-transparent w-20" />
          </div>

          <h2 className="text-5xl md:text-6xl font-bold text-white mb-8" itemProp="name">
            How We Deliver <span className="text-gradient">Elite AI Security</span>
          </h2>
          
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Our proprietary three-phase methodology ensures your organization receives enterprise-grade private AI systems with military-grade security, 
            while maintaining the performance and agility your business demands.
          </p>

          <div className="mt-8 flex justify-center space-x-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-500 mb-2">100%</div>
              <div className="text-sm text-gray-400 uppercase tracking-wide">Private Infrastructure</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-500 mb-2">0</div>
              <div className="text-sm text-gray-400 uppercase tracking-wide">Third-Party Dependencies</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-500 mb-2">24/7</div>
              <div className="text-sm text-gray-400 uppercase tracking-wide">Zero-Trust Architecture</div>
            </div>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 relative">
          {/* Connecting Line */}
          <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-yellow-500/20 to-transparent hidden lg:block" style={{ transform: 'translateY(-50%)' }} />
          
          {phases.map((phase, index) => (
            <motion.div 
              key={index} 
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              className="relative"
              itemScope 
              itemType="https://schema.org/HowToStep"
            >
              {/* Phase Number */}
              <div className="step-number">{index + 1}</div>
              
              {/* Phase Card */}
              <div className="executive-step h-full">
                {/* Icon Container */}
                <div className="executive-icon">
                  <SafeIcon icon={phase.icon} className="text-teal-400 text-3xl" />
                </div>

                {/* Phase Content */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-2xl font-bold text-yellow-500 mb-1" itemProp="name">{phase.title}</h3>
                    <h4 className="text-lg font-semibold text-white">{phase.subtitle}</h4>
                  </div>
                  
                  <p className="text-gray-300 leading-relaxed" itemProp="text">
                    {phase.description}
                  </p>

                  {/* Features List */}
                  <div className="space-y-2 pt-4">
                    {phase.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-3">
                        <div className="w-1.5 h-1.5 bg-teal-400 rounded-full flex-shrink-0" />
                        <span className="text-sm text-gray-400">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Hover Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-t from-teal-500/5 to-transparent opacity-0 transition-opacity duration-300 pointer-events-none" />
              </div>

              {/* Connection Dots for Mobile */}
              <div className="absolute top-1/2 right-0 w-2 h-2 bg-yellow-500 rounded-full transform -translate-y-1/2 translate-x-1/2 lg:hidden" />
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="mt-20 text-center"
        >
          <div className="inline-block">
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 p-8 rounded-xl border border-slate-600">
              <h3 className="text-2xl font-bold text-white mb-4">
                Ready for Enterprise-Grade AI Security?
              </h3>
              <p className="text-gray-300 mb-6">
                Schedule a confidential consultation with our elite security team
              </p>
              <a 
                href="#consultation" 
                className="cta-button-primary inline-flex items-center space-x-2"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('whitepaper')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <SafeIcon icon={FiShield} />
                <span>Schedule Elite Consultation</span>
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessSection;